import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, take } from 'rxjs';
import { AuthService } from '../msal-services/auth-service';
import { FetchSetService } from '../user-services/fetch-set-role.service';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { AppVersionService } from './app-version.service';
import { AttachmentsService } from '../../home/widget/attachments-upload/attachments.service';

@Injectable({ providedIn: 'root' })
export class AppInitService {
  _initialized = new BehaviorSubject<boolean>(false);
  readonly initialized$ = this._initialized.asObservable();
  private initialized = false;

  private _finalized$ = new BehaviorSubject<boolean>(false);
  readonly finalized$ = this._finalized$.asObservable();

  private versionCheckStarted = false;

  constructor(
    private authService: AuthService,
    private fetchSetService: FetchSetService,
    private msalBroadcastService: MsalBroadcastService,
    private ngZone: NgZone,
    private appVersionService: AppVersionService,
    private att: AttachmentsService
  ) {}

  async initApp(): Promise<void> {
    if (this.initialized) return;

    try {
      this.att.clearCache();
      // Check the deployed version BEFORE doing authentication/profile work.
      const shouldContinue = await this.appVersionService.initialize();

      if (!shouldContinue) {
        return;
      }

      await firstValueFrom(
        this.msalBroadcastService.inProgress$.pipe(
          filter((status) => status === InteractionStatus.None),
          take(1),
        ),
      );

      const isValid = await this.authService.refreshAuthState();

      if (!isValid) {
        await this.fetchSetService.getUserProfile(undefined);

        this.finishInitialization();
        return;
      }

      const account = this.authService.getActiveAccount();

      if (account) {
        await this.fetchSetService.getUserProfile(account);

        this.finishInitialization();
      }
    } catch {
      await this.fetchSetService.getUserProfile(undefined);

      this.finishInitialization();
    }
  }

  private finishInitialization(): void {
    this.initialized = true;
    this._initialized.next(true);

    this.startVersionCheck();
  }

  private startVersionCheck(): void {
    if (this.versionCheckStarted) return;

    this.versionCheckStarted = true;

    setInterval(
      () => {
        this.appVersionService.checkForUpdate();
      },
      5 * 60 * 1000,
    );
  }

  setFinalized(finalized: boolean): void {
    this.ngZone.run(() => {
      this._finalized$.next(finalized);
    });
  }

  resetApp(): void {
    this.initialized = false;
    this._initialized.next(false);
    this._finalized$.next(false);
  }
}
